import { HttpClient, HttpRequest, HttpResponse } from "@angular/common/http";
import { Injectable, inject, signal, effect } from "@angular/core";
import { SplashService } from "@dialogs/splash/splash.service";
import { ConfigService } from "@services/config/config.service";
import { HttpMethod, DownloadFormat } from "@services/download/download.enumerations";
import { DownloadService } from "@services/download/download.service";
import { SessionStorageService, SessionStorage, LocalStorage, LocalStorageService } from "ngx-webstorage";
import { tap, Observable, of, filter, map } from "rxjs";
import { UploadedDocumentInfo, FileUploadPacket, UploadedDocumentResult, SystemAlert, ApplicationsListFilter } from "./app.service.class";
import { TermInfo } from "@services/applications/applications.service.class";



@Injectable({ providedIn: 'root' })
export class AppService {
    // Injected Services
    private http = inject(HttpClient);
    private config = inject(ConfigService);
    private downloadService = inject(DownloadService);
    private localStorageService = inject(LocalStorageService);
    private sessionService = inject(SessionStorageService);
    private splashService = inject(SplashService);

    private apiGeneralUrl = this.config.hostAddress + 'api/General';
    private apiDocumentsUrl = this.config.hostAddress + 'api/Documents';

    @SessionStorage(undefined, []) private _AllSchoolYears: string[] = [];
    @SessionStorage(undefined, null) private _CurrentSchoolYear: string | null = null;
    @SessionStorage(undefined, null) private _CurrentTerm: TermInfo | null = null;
    @SessionStorage(undefined, null) private _SelectedSchoolYear: string | null = null;
    @SessionStorage(undefined, null) private _SelectedTerm: TermInfo | null = null;
    @LocalStorage(undefined) private _Filters: ApplicationsListFilter | null | undefined;

    private AllSchoolYearsSignal = signal<string[]>([]);
    private CurrentSchoolYearSignal = signal<string | null>(null);
    private CurrentTermSignal = signal<TermInfo | null>(null);
    private SelectedSchoolYearSignal = signal<string | null>(null);
    private SelectedTermSignal = signal<TermInfo | null>(null);
    private FiltersSignal = signal<ApplicationsListFilter | null | undefined>(undefined);

    readonly AllSchoolYears = this.AllSchoolYearsSignal.asReadonly();
    readonly CurrentSchoolYear = this.CurrentSchoolYearSignal.asReadonly();
    readonly CurrentTerm = this.CurrentTermSignal.asReadonly();
    readonly SelectedSchoolYear = this.SelectedSchoolYearSignal.asReadonly();
    readonly SelectedTerm = this.SelectedTermSignal.asReadonly();
    readonly Filters = this.FiltersSignal.asReadonly();

    AllSchoolYears$ = this.GetAllSchoolYears().pipe(tap(years => { this.AllSchoolYearsSignal.set(years) }));
    CurrentSchoolYear$ = this.GetCurrentSchoolYear().pipe(tap(year => {
        this.CurrentSchoolYearSignal.set(year);

        !this._SelectedSchoolYear
            ? this.SelectedSchoolYearSignal.set(year)
            : this.SelectedSchoolYearSignal.set(this._SelectedSchoolYear);
    }));

    CurrentTerm$ = this.GetCurrentTerm().pipe(tap(term => {
        this.CurrentTermSignal.set(term);

        !this._SelectedTerm
            ? this.SelectedTermSignal.set(term)
            : this.SelectedTermSignal.set(this._SelectedTerm);
    }));

    constructor() {
        const filters = this.localStorageService.retrieve('_Filters');
        if (filters) this.SetFilters(filters);
        
        effect(() => {
            this._AllSchoolYears = this.AllSchoolYearsSignal();
            this._CurrentSchoolYear = this.CurrentSchoolYearSignal();
            this._CurrentTerm = this.CurrentTermSignal();
            this._SelectedSchoolYear = this.SelectedSchoolYearSignal();
            this._SelectedTerm = this.SelectedTermSignal();
        })
    }

    ClearSessionData() {
        this.sessionService.clear()
        this.sessionService.clear('_CurrentSchoolYear');
        this.sessionService.clear('_SelectedSchoolYear');
        this.sessionService.clear('_CurrentTerm');
        this.sessionService.clear('_SelectedTerm');
        this.sessionService.clear('_AllSchoolYears');
    }

    // #region GENERAL SERVICE CALL METHODS

    GetAllSchoolYears(): Observable<string[]> {
        return this.http.get<string[]>(this.apiGeneralUrl + '/years');
        // return of(['2425', '2223', '2324']);
    }

    GetCurrentSchoolYear(): Observable<string> {
        return this.http.get<string>(this.apiGeneralUrl + '/currentyear');
    }

    GetCurrentTerm(): Observable<TermInfo | null> {
        return this.http.get<TermInfo>(this.apiGeneralUrl + '/currentterm');
    }

    SetSelectedSchoolYear(schoolyear: string | null) {
        this.SelectedSchoolYearSignal.set(schoolyear);
    }

    SetSelectedTerm(Term: TermInfo | null) {
        this.SelectedTermSignal.set(Term);
    }

    SetFilters(filters: Partial<ApplicationsListFilter>): void {
        this.FiltersSignal.update(state => ({
            ...state, 
            Text: filters.Text ?? '',
            Term: filters.Term ?? 'All',
            SchoolYear: filters.SchoolYear ?? 'All',
            Status: filters.Status ?? -1,
        }));
        this._Filters = this.FiltersSignal();
    }

    GetSystemAlert(): Observable<SystemAlert[]> {
        // return this.http.get<string[]>(this.apiGeneralUrl + '/GetSystemAlerts');
        return of([
            {
                Message: 'System Success!',
                Type: 'success',
            },
            {
                Message: 'System Error!',
                Type: 'error',
            },
            {
                Message: 'System Warning!',
                Type: 'warning',
            },
            {
                Message: 'System Info!',
                Type: 'info',
            },
        ]);
    }

    // #endregion

    // #region File Upload/Download

    // #region Get Uploaded Document

    GetUploadedDocument(item: UploadedDocumentInfo) {
        // Retrieve file from the system.
        return this.downloadService.RetrieveDownload(
            HttpMethod.GET,
            `api/Documents/file`,
            {
                UploadID: item.DocumentUploadID,
            },
            DownloadFormat.PDF
        );
    }

    // #endregion

    // #region Upload Document

    UploadDocument(file: FileUploadPacket, documentType: string, uploadID?: number) {
        const formData = new FormData();

        formData.append('Files', file.File, file.FileName);
        if (uploadID !== null && uploadID !== undefined) formData.append('UploadID', '' + uploadID);
        formData.append('DocumentType', documentType);

        const request = new HttpRequest('POST', this.apiDocumentsUrl, formData, {
            reportProgress: true,
        });

        return this.http.request(request).pipe(
            filter((event) => event instanceof HttpResponse),
            map((event) => {
                const evbody = (event as HttpResponse<UploadedDocumentResult>).body;
                if (!evbody) return evbody;
                return evbody;
            })
        );

        // return this.http.request<DocumentUploadDeleteResult>(request).pipe(
        //     filter((event) => event instanceof HttpResponse),
        //     map((event) => {
        //         const evbody = (event as HttpResponse<DocumentUploadDeleteResult>).body;
        //         if (!evbody) return evbody;

        //         evbody.UploadedDocuments?.forEach((c) => {
        //             c.UploadTimestamp = c.UploadTimestamp ? new Date(c.UploadTimestamp) : null;
        //         });

        //         // Use the provided app status updates to update the overall app status.
        //         this.appSvc.UpdateApplicationStatuses(evbody.Status);

        //         return evbody;
        //     })
        // );
    }

    // #endregion

    // #endregion
}